import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { CookieService } from 'ngx-cookie-service';
import { OAuthService } from 'angular-oauth2-oidc';
import { environment } from 'src/environments/environment';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { AppInsightsService } from '../services/appinsights.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly env = environment;
  private readonly appInsights: ApplicationInsights;
  
  // Constants for better maintainability
  private readonly EXCLUDE_LOADER_URLS = [
    'Product/GetProductsQualify',
    'api/submission/create',
    'api/track/action',
    'api/track/field',
    'api/track/actionCumField',
    'InsuranceHub/GetUserPolicyDetails/',
    'api/Nudge/GetDashboardNudgeStatus',
    'api/Nudge/InsertOrUpdateDashboardNudge',
    'api/Nudge/GetNudgeStatus',
    'api/Nudge/UpsertNudgeDate',
  ];

  private readonly SENSITIVE_FIELDS = ['Password', 'ConfirmPassword'];
  private readonly MASK_VALUE = '****';

  private totalRequests = 0;
  private completedRequests = 0;

  constructor(
    private router: Router,
    private spinner: NgxSpinnerService,
    private cookieService: CookieService,
    private authService: OAuthService,
    private appInsightsService: AppInsightsService
  ) {
    this.appInsights = appInsightsService.appInsightsObj();
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Check if loader should be shown for this request
    const shouldShowLoader = !this.shouldExcludeLoader(request.url);
    
    if (shouldShowLoader) {
      this.spinner.show();
      this.totalRequests++;
    }

    // Add headers if not Azure AD request
    if (!request.url.includes(this.env.azureAdConfig?.b2cDomain || '')) {
      request = request.clone({
        headers: this.addExtraHeaders(request.headers)
      });
    }

    return next.handle(request).pipe(
      tap(evt => this.handleSuccessResponse(evt, request)),
      catchError(err => this.handleErrorResponse(err, request)),
      finalize(() => this.handleRequestCompletion(shouldShowLoader))
    );
  }

  private shouldExcludeLoader(url: string): boolean {
    return this.EXCLUDE_LOADER_URLS.some(excludeUrl => url.includes(excludeUrl));
  }

  private handleSuccessResponse(evt: HttpEvent<any>, request: HttpRequest<any>): void {
    if (evt instanceof HttpResponse && request.urlWithParams.startsWith(this.env.webApiUrl)) {
      const sanitizedRequest = this.sanitizeRequestBody(request);
      
      this.appInsights.trackEvent({
        name: 'APIRequestResponseSuccess',
        properties: {
          URL: request.urlWithParams,
          RequestParam: sanitizedRequest.body,
          Response: evt,
          SubmissionId: sessionStorage.getItem('submissionId'),
          UserId: this.cookieService.get('userId')
        }
      });
    }
  }

  private handleErrorResponse(err: any, request: HttpRequest<any>): Observable<never> {
    if (err instanceof HttpErrorResponse) {
      if (request.urlWithParams.startsWith(this.env.webApiUrl)) {
        const sanitizedRequest = this.sanitizeRequestBody(request);
        
        this.appInsights.trackEvent({
          name: 'APIRequestResponseException',
          properties: {
            URL: request.urlWithParams,
            RequestParam: sanitizedRequest.body,
            Response: err,
            SubmissionId: sessionStorage.getItem('submissionId'),
            UserId: this.cookieService.get('userId')
          }
        });
      }

      // Handle unauthorized access
      if (err.status === 401 || err.status === 0) {
        this.clearUserSession();
        this.authService.refreshToken();
        this.authService.logOut();
      }
    }
    
    return throwError(err);
  }

  private handleRequestCompletion(shouldShowLoader: boolean): void {
    if (shouldShowLoader) {
      this.completedRequests++;
      if (this.completedRequests === this.totalRequests) {
        this.spinner.hide();
        this.completedRequests = 0;
        this.totalRequests = 0;
      }
    }
  }

  private sanitizeRequestBody(request: HttpRequest<any>): { body: any } {
    if (!request.body) {
      return { body: request.body };
    }

    const sanitizedBody = { ...request.body };
    this.SENSITIVE_FIELDS.forEach(field => {
      if (sanitizedBody.hasOwnProperty(field)) {
        sanitizedBody[field] = this.MASK_VALUE;
      }
    });

    return { body: sanitizedBody };
  }

  private clearUserSession(): void {
    // Clear cookies
    ['submissionId', 'uType', 'userId'].forEach(cookie => {
      this.cookieService.delete(cookie, '/');
    });
    
    // Clear storage
    localStorage.removeItem('isLoginSuccess');
    sessionStorage.clear();
  }

  private addExtraHeaders(headers: HttpHeaders): HttpHeaders {
    const submissionId = sessionStorage.getItem('submissionId') || '';
    
    headers = headers
      .append('SubmissionId', submissionId)
      .append('Cache-Control', 'no-cache')
      .append('Pragma', 'no-cache')
      .append('Expires', 'Sat, 01 Jan 2000 00:00:00 GMT')
      .append('Version', 'V1');

    const authToken = this.getAuthorizationToken();
    if (authToken) {
      headers = headers.append('Authorization', authToken);
    }

    return headers;
  }

  private getAuthorizationToken(): string {
    const tempToken = localStorage.getItem('tempToken');
    const authToken = this.cookieService.get('authToken');
    
    if (tempToken) {
      return `Bearer ${tempToken}`;
    }
    
    if (authToken) {
      return `Bearer ${authToken}`;
    }
    
    return '';
  }
}
